import React, { useState, useEffect, useRef } from 'react';
import { LOADING_CIRCLE } from 'common/utils/constants';
import { searchDevicesByIdV2 } from 'apis/SearchDevices';
import { DebounceInput } from 'react-debounce-input';
import classnames from 'classnames';

import './CommissionDeviceQuery.css';

const BUNDLED_DEVICE_NOTE_MESSAGE = 'This device has been commissioned with the one you selected.';
const COMMISSIONED_DEVICE_ERROR_MESSAGE = 'Previously commissioned device cannot be used.';

const CommissionDeviceQuery = (props) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [noDeviceFound, setNoDeviceFound] = useState(false);
  const [inputFocus, setInputFocus] = useState(false);
  const [queryRes, setQueryRes] = useState([]);
  const [actionPending, setActionPending] = useState(false);

  // deconstruct according to ESLint react recommendations for hooks
  const {
    productType,
    isBundled,
    deviceIds,
    devicesData,
    deviceIndex,
    errors,
    errorOnChange,
    inputOnChange,
    updateDevicesData,
    updateBundleMembers,
    readOnly
  } = props;

  const deviceIdsRef = useRef(deviceIds);
  const devicesDataRef = useRef(devicesData);

  useEffect(() => {
    deviceIdsRef.current = deviceIds;
    devicesDataRef.current = devicesData;
  });

  useEffect(() => {
    const currentId = deviceIdsRef.current[deviceIndex];
    if (!currentId || currentId.length === 0) {
      if (deviceIndex !== 2) {
        errorOnChange('Device ID cannot be empty.', deviceIndex);
        updateDevicesData({}, deviceIndex);
      } else {
        errorOnChange('', deviceIndex);
        updateDevicesData({}, deviceIndex);
      }
      return;
    }
    setActionPending(true);
    setNoDeviceFound(false);
    searchDevicesByIdV2(currentId, productType).then((devices) => {
      let errorMessage = '';
      setQueryRes(devices);

      if (!devices) return;
      if (devices.length === 0) {
        setNoDeviceFound(true);
        errorMessage = 'Device ID is invalid.';
      } else if (devices.length === 1 || devices[0].deviceId === currentId) {
        if (devices[0].deviceId !== currentId) {
          errorMessage = 'Device ID is invalid.';
        } else if (isDuplicateDeviceId(currentId)) {
          errorMessage = 'Device ID is already selected.';
        } else if (devices[0].connectionState !== 'Connected') {
          errorMessage =
            'This device is currently offline. Please wait till it is back online and select this device again.';
        } else if (devices[0].adminState !== 'unlocked') {
          errorMessage = 'Device service state unavailable. Unable to proceed.';
        } else if (devices[0].hasCriticalAlarms) {
          errorMessage = 'Critical alarm exists for this device. Unable to proceed.';
        } else if (devices[0].isBundled) {
          if (
            devicesDataRef.current.some(
              (device, index) => index !== deviceIndex && Object.keys(device).length !== 0 && !device.isBundled
            )
          ) {
            errorMessage = COMMISSIONED_DEVICE_ERROR_MESSAGE;
          } else {
            // auto-fill member
            updateBundleMembers(devices);
            errorMessage = BUNDLED_DEVICE_NOTE_MESSAGE;
          }
        } else {
          if (
            devicesDataRef?.current &&
            devicesDataRef.current.some((device, index) => index !== deviceIndex && device.isBundled)
          ) {
            // case: a not-commissioned device is selected given other inputs have bundled devices
            // update input error for bundled devices
            devicesDataRef.current.forEach((device, index) => {
              if (index !== deviceIndex && device.isBundled) {
                errorOnChange(COMMISSIONED_DEVICE_ERROR_MESSAGE, index);
              }
            });
          }
          updateDevicesData(devices[0], deviceIndex);
        }

        setNoDeviceFound(false);
      } else if (devices.length > 1) {
        errorMessage = 'Device ID is invalid.';
        setNoDeviceFound(false);
      }
      if (isInitialLoad) setIsInitialLoad(false);
      if (errorMessage !== BUNDLED_DEVICE_NOTE_MESSAGE) {
        errorOnChange(errorMessage, deviceIndex);
      }

      if (errorMessage !== '' && errorMessage !== BUNDLED_DEVICE_NOTE_MESSAGE) {
        updateDevicesData({}, deviceIndex);
      }
      setActionPending(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIds[deviceIndex]]);

  useEffect(() => {
    const errorMessage = '';
    if (errors[deviceIndex] === 'Device ID is already selected.') {
      if (!isDuplicateDeviceId(deviceIds[deviceIndex])) {
        errorOnChange(errorMessage, deviceIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIds, deviceIndex]);

  const isDuplicateDeviceId = (targetString) => {
    if (targetString === '') return false;
    let counter = 0;
    deviceIdsRef.current.forEach((id) => {
      if (id === targetString) {
        counter += 1;
      }
    });
    return counter > 1;
  };

  const getSearchDevicesDropdown = () => {
    if (!deviceIds[deviceIndex] || deviceIds[deviceIndex].length < 1) return null;
    return (
      <div className="CreateSite_DropdownContainer">
        <div className="CreateSite_DropdownUl">
          {actionPending && <div className="Global_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>}

          {!actionPending &&
            queryRes &&
            queryRes.map(({ deviceId, isBundled }, index) => {
              return (
                <div
                  className={'CreateSite_DropdownLi'}
                  key={index}
                  onMouseDown={() => {
                    setInputFocus(false);
                    inputOnChange(deviceId, deviceIndex);
                  }}
                >
                  {/* <div className="CreateSiteDropdown_Query">{deviceIds[deviceIndex]}</div> */}
                  <div className="CreateSiteDropdown_String">{deviceId}</div>
                  {isBundled && <div className="CreateSiteDropdown_IsBundled">{'Commissioned'}</div>}
                </div>
              );
            })}
          {noDeviceFound && <div className="DeviceSearchBarDropdown_NoDeviceFoundText">No devices match search.</div>}
        </div>
      </div>
    );
  };

  return !props.fromPivotOverView ? (
    <div className={'CreateSiteModal_SiteInputContainer'} key={deviceIndex}>
      <div
        className={classnames({
          CreateSiteModal_SiteInput: true,
          CreateSiteModal_SiteInput_Error:
            !inputFocus &&
            !isInitialLoad &&
            errors[deviceIndex] !== '' &&
            errors[deviceIndex] !== BUNDLED_DEVICE_NOTE_MESSAGE,
          CreateSiteModal_InputReadOnly: readOnly === true
        })}
      >
        <DebounceInput
          disabled={props.fromPivotOverView}
          onChange={(evt) => {
            if (isInitialLoad) setIsInitialLoad(false);
            inputOnChange(evt.target.value, deviceIndex);
          }}
          value={props.targetDeviceId ? props.targetDeviceId : deviceIds[deviceIndex]}
          debounceTimeout={1000}
          minLength={1}
          forceNotifyByEnter={true}
          forceNotifyOnBlur={true}
          onFocus={() => setInputFocus(true)}
          onBlur={() => setInputFocus(false)}
          readOnly={readOnly === true}
        />
        {(isBundled || errors[deviceIndex] === COMMISSIONED_DEVICE_ERROR_MESSAGE) && (
          <div className="CreateSiteDropdown_Input_IsBundled">{'Commissioned'}</div>
        )}
      </div>
      {inputFocus && getSearchDevicesDropdown()}
      {!inputFocus && errors[deviceIndex] !== '' && !isInitialLoad && (
        <div
          className={classnames({
            CreateSiteModal_InputError: errors[deviceIndex] !== BUNDLED_DEVICE_NOTE_MESSAGE,
            CreateSiteModal_InputNote: errors[deviceIndex] === BUNDLED_DEVICE_NOTE_MESSAGE
          })}
        >
          {errors[deviceIndex]}
        </div>
      )}
    </div>
  ) : (
    <div className={'CreateSiteModal_SiteContainer'} key={deviceIndex}>
      {deviceIds[deviceIndex]}
      {!inputFocus && errors[deviceIndex] !== '' && !isInitialLoad && (
        <div
          className={classnames({
            CreateSiteModal_InputError: errors[deviceIndex] !== BUNDLED_DEVICE_NOTE_MESSAGE,
            CreateSiteModal_InputNote: errors[deviceIndex] === BUNDLED_DEVICE_NOTE_MESSAGE
          })}
        >
          {errors[deviceIndex]}
        </div>
      )}
    </div>
  );
};

export default CommissionDeviceQuery;
